import React, { useState, useEffect, useRef } from 'react';
import { useApps } from '@/contexts/AppsContext';
import { recallApi, mindApi } from '@/services/api';
import {
  Search,
  Loader2,
  Brain,
  ChevronDown,
  ChevronRight,
  Sparkles,
  Target,
  GitBranch,
} from 'lucide-react';

// ==========================================
// SEED NODE CARD
// ==========================================

function SeedCard({ seed, index }) {
  const isContent = seed.nodeType === 'ContentAtom';
  const label = isContent ? (seed.snippet || '(no snippet)') : (seed.label || seed.id);
  const score = typeof seed.score === 'number' ? seed.score.toFixed(3) : '?';

  return (
    <div className="border rounded-lg p-3 bg-card/50 hover:bg-card/80 transition-colors">
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="flex items-center gap-1.5">
          <span className={`w-2 h-2 rounded-full shrink-0 ${
            isContent ? 'bg-blue-500' : 'bg-purple-500'
          }`} />
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
            {seed.nodeType === 'ContentAtom' ? 'Content' : 'Concept'}
          </span>
        </div>
        <span className="text-[10px] font-mono text-muted-foreground shrink-0">
          {score}
        </span>
      </div>
      <p className="text-xs text-foreground leading-relaxed line-clamp-3">
        {label}
      </p>
      {seed.source && (
        <p className="text-[10px] text-muted-foreground mt-1 truncate">
          Source: {seed.source}
        </p>
      )}
    </div>
  );
}

// ==========================================
// EDGE CARD
// ==========================================

function EdgeCard({ edge }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="text-xs border-b border-border/50 py-1.5">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1.5 w-full text-left hover:text-foreground transition-colors"
      >
        {expanded ? (
          <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" />
        ) : (
          <ChevronRight className="h-3 w-3 text-muted-foreground shrink-0" />
        )}
        <span className="text-muted-foreground font-mono truncate max-w-[100px]">
          {edge.sourceId?.slice(0, 12)}
        </span>
        <span className="text-orange-400 font-medium shrink-0">{edge.type}</span>
        <span className="text-muted-foreground font-mono truncate max-w-[100px]">
          {edge.targetId?.slice(0, 12)}
        </span>
        {edge.weight != null && (
          <span className="text-muted-foreground ml-auto shrink-0">({edge.weight})</span>
        )}
      </button>
      {expanded && edge.quote && (
        <p className="text-xs text-foreground/80 mt-1 ml-4 whitespace-pre-wrap leading-relaxed">
          {edge.quote}
        </p>
      )}
    </div>
  );
}

// ==========================================
// SYNTHESIS SECTION
// ==========================================

function SynthesisSection({ summary }) {
  if (!summary) return null;

  // Simple markdown-ish rendering (## headers, **bold**, bullet points)
  const lines = summary.split('\n');

  return (
    <div className="space-y-2">
      {lines.map((line, i) => {
        const trimmed = line.trim();
        if (!trimmed) return <div key={i} className="h-2" />;

        if (trimmed.startsWith('## ')) {
          return (
            <h3 key={i} className="text-sm font-semibold text-foreground mt-3 first:mt-0">
              {trimmed.slice(3)}
            </h3>
          );
        }

        if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
          return (
            <div key={i} className="flex gap-1.5 text-xs text-foreground/90 leading-relaxed">
              <span className="text-muted-foreground mt-0.5 shrink-0">-</span>
              <span>{renderInlineMarkdown(trimmed.slice(2))}</span>
            </div>
          );
        }

        return (
          <p key={i} className="text-xs text-foreground/90 leading-relaxed">
            {renderInlineMarkdown(trimmed)}
          </p>
        );
      })}
    </div>
  );
}

function renderInlineMarkdown(text) {
  // Bold: **text** → <strong>
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i} className="font-medium text-foreground">{part.slice(2, -2)}</strong>;
    }
    return part;
  });
}

// ==========================================
// CONTEXT TAB (MAIN COMPONENT)
// ==========================================

export default function ContextTab() {
  const { currentApp } = useApps();
  const appId = currentApp?.appId;

  // Root node state
  const [roots, setRoots] = useState([]);
  const [selectedRoot, setSelectedRoot] = useState(null);

  // Query state
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);

  // Section collapse state
  const [sectionsOpen, setSectionsOpen] = useState({
    synthesis: true,
    seeds: true,
    evidence: false,
  });

  const inputRef = useRef(null);

  // Fetch root nodes
  useEffect(() => {
    if (!appId) return;
    mindApi.getRoots(appId)
      .then(res => {
        const allRoots = Array.isArray(res.data?.data) ? res.data.data : [];
        setRoots(allRoots);
        if (allRoots.length === 1) setSelectedRoot(allRoots[0].entityId);
      })
      .catch(() => setRoots([]));
  }, [appId]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const toggleSection = (section) => {
    setSectionsOpen(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Submit recall query
  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!query.trim() || loading || !appId || !selectedRoot) return;

    setLoading(true);
    setError(null);
    setResult(null);

    const startTime = Date.now();

    try {
      const response = await recallApi.query(appId, {
        query: query.trim(),
        root_id: selectedRoot,
        maxResults: 10,
        expansionDepth: 2,
      });

      const data = response.data?.data;

      setResult(data);
      setHistory(prev => [{
        query: query.trim(),
        timestamp: new Date().toISOString(),
        seedCount: data.seeds?.length || 0,
        latency: data.latency_ms || (Date.now() - startTime),
      }, ...prev].slice(0, 10));

      // Auto-expand synthesis, collapse others
      setSectionsOpen({ synthesis: true, seeds: true, evidence: false });
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Recall failed';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Query Input */}
      <form onSubmit={handleSubmit} className="p-4 border-b shrink-0">
        <div className="flex gap-2 w-full">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Ask about the graph... (e.g., 'What does Dustin think about AI?')"
              className="w-full pl-9 pr-3 py-2.5 text-sm bg-background border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-foreground placeholder:text-muted-foreground"
              disabled={loading || !appId || !selectedRoot}
            />
          </div>
          <select
            value={selectedRoot || ''}
            onChange={(e) => setSelectedRoot(e.target.value || null)}
            className="px-3 py-2.5 text-sm bg-background border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-foreground shrink-0"
          >
            <option value="">Select root...</option>
            {roots.map(r => (
              <option key={r.entityId} value={r.entityId}>
                {r.name || r.entityId}
              </option>
            ))}
          </select>
          <button
            type="submit"
            disabled={!query.trim() || loading || !appId || !selectedRoot}
            className="px-4 py-2.5 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shrink-0"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4" />
            )}
            Recall
          </button>
        </div>
      </form>

      {/* Results */}
      <div className="flex-1 overflow-y-auto">
        {/* Error */}
        {error && (
          <div className="m-4 p-3 text-sm text-destructive bg-destructive/5 border border-destructive/20 rounded-lg">
            {error}
          </div>
        )}

        {/* Loading state */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <div className="relative">
              <Brain className="h-8 w-8 text-primary animate-pulse" />
            </div>
            <p className="text-sm text-muted-foreground">Searching memory...</p>
            <div className="flex gap-2 text-[10px] text-muted-foreground">
              <span>Embedding query</span>
              <span>-</span>
              <span>Vector search</span>
              <span>-</span>
              <span>Graph expansion</span>
              <span>-</span>
              <span>Synthesis</span>
            </div>
          </div>
        )}

        {/* Empty state */}
        {!loading && !result && !error && (
          <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
            <Brain className="h-12 w-12 mb-4 opacity-30" />
            <p className="text-sm font-medium mb-1">Semantic Recall</p>
            <p className="text-xs text-center max-w-sm">
              Enter a question, topic, or passage. The system will search the cognitive graph
              using vector similarity and spreading activation, then synthesize a response
              grounded in evidence.
            </p>
            {history.length > 0 && (
              <div className="mt-6 w-full max-w-sm">
                <p className="text-[10px] uppercase tracking-wider font-semibold mb-2">Recent Queries</p>
                <div className="space-y-1">
                  {history.map((h, i) => (
                    <button
                      key={i}
                      onClick={() => { setQuery(h.query); }}
                      className="w-full text-left text-xs py-1 px-2 rounded hover:bg-accent/50 transition-colors truncate"
                    >
                      {h.query}
                      <span className="text-muted-foreground/60 ml-2">{h.seedCount} seeds, {h.latency}ms</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Results */}
        {result && !loading && (
          <div className="p-4 space-y-4">
            {/* Latency badge */}
            <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
              <span>{result.seeds?.length || 0} seeds</span>
              <span>-</span>
              <span>{result.context?.nodes?.length || 0} nodes expanded</span>
              <span>-</span>
              <span>{result.context?.edges?.length || 0} edges</span>
              <span>-</span>
              <span>{result.latency_ms}ms</span>
            </div>

            {/* Synthesis */}
            <div className="border rounded-lg overflow-hidden">
              <button
                onClick={() => toggleSection('synthesis')}
                className="w-full flex items-center gap-2 p-3 hover:bg-accent/30 transition-colors"
              >
                {sectionsOpen.synthesis ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                )}
                <Sparkles className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium text-foreground">Synthesis</span>
              </button>
              {sectionsOpen.synthesis && (
                <div className="px-4 pb-4 border-t">
                  <div className="pt-3">
                    <SynthesisSection summary={result.synthesis?.summary} />
                  </div>
                </div>
              )}
            </div>

            {/* Seeds */}
            {result.seeds?.length > 0 && (
              <div className="border rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleSection('seeds')}
                  className="w-full flex items-center gap-2 p-3 hover:bg-accent/30 transition-colors"
                >
                  {sectionsOpen.seeds ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  )}
                  <Target className="h-4 w-4 text-blue-500" />
                  <span className="text-sm font-medium text-foreground">
                    Seed Nodes ({result.seeds.length})
                  </span>
                  <span className="text-[10px] text-muted-foreground ml-auto">vector matches</span>
                </button>
                {sectionsOpen.seeds && (
                  <div className="px-3 pb-3 border-t">
                    <div className="pt-3 grid gap-2">
                      {result.seeds.map((seed, i) => (
                        <SeedCard key={seed.id || i} seed={seed} index={i} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Evidence Graph */}
            {(result.context?.edges?.length > 0 || result.context?.nodes?.length > 0) && (
              <div className="border rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleSection('evidence')}
                  className="w-full flex items-center gap-2 p-3 hover:bg-accent/30 transition-colors"
                >
                  {sectionsOpen.evidence ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  )}
                  <GitBranch className="h-4 w-4 text-green-500" />
                  <span className="text-sm font-medium text-foreground">
                    Evidence Graph ({result.context.edges?.length || 0} edges)
                  </span>
                  <span className="text-[10px] text-muted-foreground ml-auto">graph expansion</span>
                </button>
                {sectionsOpen.evidence && (
                  <div className="px-3 pb-3 border-t">
                    <div className="pt-3">
                      {/* Nodes summary */}
                      {result.context.nodes?.length > 0 && (
                        <div className="mb-3">
                          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">
                            Connected Entities ({result.context.nodes.length})
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {result.context.nodes.slice(0, 20).map((node, i) => (
                              <span
                                key={node.id || i}
                                className="px-2 py-0.5 text-[10px] rounded-full bg-accent/50 text-foreground"
                              >
                                {node.label || node.id?.slice(0, 12)}
                              </span>
                            ))}
                            {result.context.nodes.length > 20 && (
                              <span className="px-2 py-0.5 text-[10px] rounded-full bg-accent/30 text-muted-foreground">
                                +{result.context.nodes.length - 20} more
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Edges list */}
                      {result.context.edges?.length > 0 && (
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">
                            Relationships
                          </p>
                          <div className="space-y-0">
                            {result.context.edges.map((edge, i) => (
                              <EdgeCard key={i} edge={edge} />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* No results */}
            {(!result.seeds || result.seeds.length === 0) && (
              <div className="text-center py-8 text-muted-foreground">
                <p className="text-sm">No matching content found.</p>
                <p className="text-xs mt-1">
                  Content may need to be re-ingested with embeddings enabled.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
